import { enumUnit } from '@rematter/frontend-lib-api-client';
import { weightToPounds } from '@rematter/ui-web';

import { NormalizedDeduction, NormalizedLineItem } from '../_types';

import { enumLoadWeightType } from '~/modules/trade/types/load';

export type WeightSummaryResult = {
  netWeightBeforeDeductionsLbs: number;
  deductionsWeight: number;
  netWeightAfterDeductions: number;
  outstandingNetWeight: number;
  tareOnTruck: number;
};

export const calculateWeightSummary = (
  lineItems: NormalizedLineItem[],
  deductions: NormalizedDeduction[],
  heavyWeightLbs: number,
  lightWeightLbs: number,
): WeightSummaryResult => {
  const itemDeductions = deductions.filter(
    (d) => d.purchaseLoadLineItemId != null,
  );
  const purchaseDeductions = deductions.filter(
    (d) => d.purchaseLoadLineItemId == null,
  );

  const itemCalcs = lineItems.map((item) => {
    const isEach = item.pricingUnit === enumUnit.Each;
    const grossWeight = item.weights?.[enumLoadWeightType.Gross];
    const tareWeight = item.weights?.[enumLoadWeightType.Tare];
    const grossWeightLbs = grossWeight ? weightToPounds(grossWeight) : 0;
    const tareWeightLbs = tareWeight ? weightToPounds(tareWeight) : 0;
    const netWeightLbs = isEach ? 0 : grossWeightLbs - tareWeightLbs;

    const thisItemDeductions = itemDeductions.filter(
      (d) => d.purchaseLoadLineItemId === item.id,
    );

    const weightDeductionLbs = thisItemDeductions.reduce((acc, d) => {
      if (d.applicationType === 'Weight') {
        const deductWeightLbs = d.weightValue
          ? weightToPounds(d.weightValue)
          : 0;
        return acc + deductWeightLbs;
      }
      return acc;
    }, 0);

    return {
      netWeightLbs,
      weightDeductionLbs,
    };
  });

  const { netWeightBeforeDeductionsLbs, totalItemWeightDeductionLbs } =
    itemCalcs.reduce(
      (acc, c) => ({
        netWeightBeforeDeductionsLbs:
          acc.netWeightBeforeDeductionsLbs + c.netWeightLbs,
        totalItemWeightDeductionLbs:
          acc.totalItemWeightDeductionLbs + c.weightDeductionLbs,
      }),
      {
        netWeightBeforeDeductionsLbs: 0,
        totalItemWeightDeductionLbs: 0,
      },
    );

  const purchaseWeightLbs = purchaseDeductions.reduce((acc, d) => {
    if (d.applicationType === 'Weight') {
      const deductWeightLbs = d.weightValue ? weightToPounds(d.weightValue) : 0;
      return acc + deductWeightLbs;
    }
    return acc;
  }, 0);

  const deductionsWeight = totalItemWeightDeductionLbs + purchaseWeightLbs;
  const netWeightAfterDeductions =
    netWeightBeforeDeductionsLbs - deductionsWeight;
  const tareOnTruck = lineItems
    .filter((it) => it.tareOnTruck)
    .reduce((sum, item) => {
      const tareWeight = item.weights?.[enumLoadWeightType.Tare];
      return sum + weightToPounds(tareWeight);
    }, 0);

  const outstandingNetWeight =
    heavyWeightLbs > 0
      ? heavyWeightLbs -
        lightWeightLbs -
        netWeightAfterDeductions -
        deductionsWeight -
        tareOnTruck
      : 0;

  return {
    netWeightBeforeDeductionsLbs,
    deductionsWeight,
    netWeightAfterDeductions,
    outstandingNetWeight,
    tareOnTruck,
  };
};
